import { FetchHelper } from "shared-ts-utils-fetch";
import { AccessToken } from "shared-ts-utils-authentication";
import { CustomerEnvironmentName } from "firefly-shared-ts-customer-environment";
import {
  IllegalStateError,
  NotFoundError,
  NullError,
} from "shared-ts-utils-core";
import { FlueStatus, FlueStatusDto } from "./FlueStatus";
import { FlueStatusQuery } from "./FlueStatusQuery";
import { anonymizeStreetAddress, FlueId } from "firefly-shared-ts-domain";
import { QueryDto, QueryResult } from "shared-ts-utils-query";

const anonymize = localStorage.getItem("anonymize") === "true";

export class FlueStatusDao {
  fetchHelper: FetchHelper;

  constructor(args: {
    accessToken: AccessToken;
    customerEnvironment: CustomerEnvironmentName;
  }) {
    this.fetchHelper = new FetchHelper({ basePath: "/api/" })
      .authorizationBearer(args.accessToken)
      .addHeader("Firefly-Environment", args.customerEnvironment);
  }

  getById(id: FlueId): Promise<FlueStatus> {
    const query = new FlueStatusQuery({
      id: id,
    });
    return this.querySingle(query).then((result) => {
      if (result === undefined) {
        throw new NotFoundError("Did not find Flue with id: " + id);
      } else {
        return result;
      }
    });
  }

  query(query: QueryDto): Promise<QueryResult<FlueStatus>> {
    return this.fetchHelper
      .post("flue-status/query", query)
      .fetchAsJson<QueryResult<FlueStatusDto>>()
      .then((result) => {
        if (result.result == null) {
          throw new NullError(
            "QueryResult contains not data: \n" + JSON.stringify(result)
          );
        }

        if (anonymize) {
          return {
            ...result,
            result: result.result.map((it) => {
              return new FlueStatus({
                ...it,
                streetAddress: anonymizeStreetAddress(it.streetAddress, it.id),
              });
            }),
          };
        }
        return {
          ...result,
          result: result.result.map((it) => new FlueStatus(it)),
        };
      });
  }

  querySingle(query: FlueStatusQuery): Promise<FlueStatus | undefined> {
    return this.fetchHelper
      .post("flue-status/query", query.toDto())
      .fetchAsJson<QueryResult<FlueStatusDto>>()
      .then((response) => {
        if (response.result == null) {
          throw new NullError(
            "QueryResult contains not data: \n" + JSON.stringify(response)
          );
        }
        if (response.result.length === 0) {
          return undefined;
        }
        if (response.result.length > 1) {
          throw new IllegalStateError(
            `Query single Flue ${JSON.stringify(
              query
            )}, unexpectedly resulted in more than one: ` +
              response.result.length
          );
        }

        return new FlueStatus(response.result[0]);
      });
  }
}
